/**
 * PURE option builder for the unified Bar & Column family.
 *
 * No Angular / no ECharts-runtime dependency — takes capabilities + flat rows and
 * returns a plain EChartsOption object, so all variant/orientation/combo/dual-axis
 * logic is unit-testable in isolation. The component is just a lifecycle shell
 * around `buildBarColumnOption`.
 *
 * Variant → ECharts mapping (see BarColumnVariant):
 *   simple      → single series, no stack.
 *   grouped     → one series per `series` value, side-by-side (no stack).
 *   clustered   → alias of grouped.
 *   stacked     → one series per `series` value, stack:'total'.
 *   stacked-100 → stacked, each category normalized to 100%.
 * Orientation → axis roles: vertical = category on X / value on Y; horizontal
 * swaps them. Per-measure renderAs (bar|line|area) + axis (y1|y2) give the
 * Line+Column / Area+Column / Dual-Axis combinations.
 */
import type { EChartsOption } from 'echarts';
import { toCapitalCase } from '../../utils/text-case.utils';
import { applyChartFont } from '../../utils/chart-font.utils';
import type {
  BarColumnCapabilities,
  BarColumnMeasure,
  BarColumnReferenceLine,
} from './unified-bar-column-chart.types';

type Row = Record<string, unknown>;
type Agg = NonNullable<BarColumnCapabilities['aggregation']>;

const DEFAULT_PALETTE = ['#1f6feb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

/** Numeric coercion tolerant of "1,234" and "$1.2k"-style strings → the raw
 *  numeric part; non-numeric → 0 so a bad cell never NaNs the whole series. */
function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function aggregate(values: number[], agg: Agg): number {
  if (!values.length) return 0;
  switch (agg) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    case 'sum':
    default: return values.reduce((a, b) => a + b, 0);
  }
}

/** Distinct values of a column, in first-seen order. */
function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((row) => {
    const key = String(row[field] ?? '');
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  });
  return out;
}

/** Confidence tiers have an ordinal meaning that must not depend on the API's
 * row order. Keeping this order stable also keeps their palette colors stable
 * across horizontal and vertical versions of the same chart. Other series
 * retain their response order. */
function orderConfidenceTiers(seriesNames: string[]): string[] {
  const tierRank = (name: string): number | undefined => {
    const normalized = name.trim().toLowerCase();
    if (/^high\s*(?:\(|$)/.test(normalized)) return 0;
    if (/^(?:med|medium)\s*(?:\(|$)/.test(normalized)) return 1;
    if (/^low\s*(?:\(|$)/.test(normalized)) return 2;
    return undefined;
  };

  const ranked = seriesNames.map((name, index) => ({ name, index, rank: tierRank(name) }));
  if (!ranked.length || ranked.some(({ rank }) => rank === undefined)) return seriesNames;

  return ranked
    .sort((a, b) => (a.rank! - b.rank!) || (a.index - b.index))
    .map(({ name }) => name);
}

/** Lightweight number formatter (kept local so the builder has no Angular dep).
 *  Mirrors the presets the DVM tiles use elsewhere. */
function formatValue(value: number, capabilities: BarColumnCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));

  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }

  // Applied everywhere formatValue is used (axis ticks, tooltip, segment
  // labels, stack totals) so setting it once covers the whole chart.
  const prefix = capabilities.valuePrefix || '';

  if (fmt === 'PERCENT' || fmt === 'percent') {
    return `${prefix}${(value * 100).toFixed(decimals)}%`;
  }
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${prefix}${body}${suffix}`;
}

/** One aggregated series: category → value grid. Result[categoryIndex] is the
 *  aggregated number for that category (missing cells → 0). */
function aggregateByCategory(rows: Row[], category: string, value: string, categories: string[], agg: Agg): number[] {
  const buckets = new Map<string, number[]>();
  rows.forEach((row) => {
    const cat = String(row[category] ?? '');
    const list = buckets.get(cat) || [];
    list.push(toNumber(row[value]));
    buckets.set(cat, list);
  });
  return categories.map((cat) => aggregate(buckets.get(cat) || [], agg));
}

/** category × series grid: [seriesName] → per-category aggregated values. */
function aggregateByCategoryAndSeries(
  rows: Row[], category: string, value: string, series: string, categories: string[], seriesNames: string[], agg: Agg,
): Map<string, number[]> {
  const buckets = new Map<string, number[]>(); // key = `${series}||${cat}`
  rows.forEach((row) => {
    const cat = String(row[category] ?? '');
    const ser = String(row[series] ?? '');
    const key = `${ser}||${cat}`;
    const list = buckets.get(key) || [];
    list.push(toNumber(row[value]));
    buckets.set(key, list);
  });
  const out = new Map<string, number[]>();
  seriesNames.forEach((ser) => {
    out.set(ser, categories.map((cat) => aggregate(buckets.get(`${ser}||${cat}`) || [], agg)));
  });
  return out;
}

/** Round UP to a "nice" axis-friendly ceiling (1/2/5 × a power of 10) — e.g.
 *  150 → 200, 39 → 50, 4 → 5. Used to pin the value axis's max to a value the
 *  invisible stack-total track can reach exactly, instead of ECharts' own
 *  (slightly different) auto-computed "nice" max leaving a gap. */
function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = Math.pow(10, exp);
  const norm = value / base;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return niceNorm * base;
}

/** Normalize each category column to 100% across the series (for stacked-100). */
function normalizeTo100(grids: number[][]): number[][] {
  if (!grids.length) return grids;
  const categoryCount = grids[0].length;
  const totals: number[] = [];
  for (let c = 0; c < categoryCount; c++) {
    let sum = 0;
    for (const g of grids) sum += Math.abs(g[c] || 0);
    totals[c] = sum;
  }
  return grids.map((g) => g.map((v, c) => (totals[c] ? (v / totals[c]) * 100 : 0)));
}

/** markLine data for the configured reference lines, on the value axis. */
function referenceMarkLine(refs: BarColumnReferenceLine[] | undefined, horizontal: boolean, defaultColor: string, lineType: string): Record<string, unknown> | undefined {
  if (!refs?.length) return undefined;
  return {
    silent: true,
    symbol: 'none',
    data: refs.map((ref) => ({
      // xAxis for a value-on-X (horizontal) chart, yAxis for value-on-Y (vertical).
      ...(horizontal ? { xAxis: ref.value } : { yAxis: ref.value }),
      label: { formatter: ref.label || String(ref.value), position: 'insideEndTop' },
      lineStyle: { color: ref.color || defaultColor, type: lineType },
    })),
  };
}

/** Map a tooltip position preset to an ECharts `tooltip.position` value.
 *  'auto'/'follow' → let ECharts follow the cursor (undefined). 'fixed-tr' →
 *  pin to the top-right. Directional presets return a positioner function that
 *  offsets from the point. */
function tooltipPositionFor(preset: string | undefined): unknown {
  switch (preset) {
    case 'top': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] / 2, point[1] - size.contentSize[1] - 12];
    case 'bottom': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] / 2, point[1] + 18];
    case 'left': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] - size.contentSize[0] - 12, point[1] - size.contentSize[1] / 2];
    case 'right': return (point: number[], _p: unknown, _dom: unknown, _r: unknown, size: { contentSize: number[] }) => [point[0] + 18, point[1] - size.contentSize[1] / 2];
    case 'fixed-tr': return (_pt: unknown, _p: unknown, _dom: unknown, _r: unknown, size: { viewSize: number[]; contentSize: number[] }) => [size.viewSize[0] - size.contentSize[0] - 8, 8];
    case 'follow':
    case 'auto':
    default: return undefined;
  }
}

/** Build an HTML tooltip that includes a small inline bar per series/measure for
 *  the hovered category — the "chart in tooltip". Uses plain divs (no nested
 *  ECharts) so it's cheap and self-contained. */
function miniChartTooltipFormatter(
  style: Record<string, string | number | undefined>,
  fmt: (v: number) => string,
): (params: unknown) => string {
  // All micro-layout constants come from config (with fallbacks).
  const colorFallback = String(style['seriesColorFallback'] ?? '#1f6feb');
  const trackBg = String(style['trackBg'] ?? 'rgba(148,163,184,.25)');
  const minWidth = Number(style['minWidth'] ?? 180);
  const labelWidth = Number(style['labelWidth'] ?? 70);
  const barMinWidth = Number(style['barMinWidth'] ?? 60);
  const barHeight = Number(style['barHeight'] ?? 8);
  return (params: unknown): string => {
    const list = (Array.isArray(params) ? params : [params]) as Array<{
      name?: string; seriesName?: string; value?: unknown; color?: string;
    }>;
    if (!list.length) return '';
    const title = String(list[0]?.name ?? '');
    const rows = list.map((p) => ({ name: String(p.seriesName ?? ''), value: Number(p.value ?? 0), color: p.color || colorFallback }));
    const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)));
    const bars = rows
      .map((r) => {
        const pct = Math.max(2, Math.round((Math.abs(r.value) / max) * 100));
        return (
          `<div style="display:flex;align-items:center;gap:6px;margin:2px 0;">` +
          `<span style="flex:0 0 ${labelWidth}px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${r.name}</span>` +
          `<span style="flex:1 1 auto;min-width:${barMinWidth}px;height:${barHeight}px;background:${trackBg};border-radius:4px;overflow:hidden;">` +
          `<span style="display:block;height:100%;width:${pct}%;background:${r.color};border-radius:4px;"></span></span>` +
          `<span style="flex:0 0 auto;font-weight:600;">${fmt(r.value)}</span>` +
          `</div>`
        );
      })
      .join('');
    return `<div style="min-width:${minWidth}px;"><div style="font-weight:700;margin-bottom:4px;">${title}</div>${bars}</div>`;
  };
}

/** Resolve the effective ECharts series `type` + area flag from a measure. */
function seriesTypeFor(measure: BarColumnMeasure): { type: 'bar' | 'line'; area: boolean } {
  const renderAs = measure.renderAs || 'bar';
  if (renderAs === 'line') return { type: 'line', area: false };
  if (renderAs === 'area') return { type: 'line', area: true };
  return { type: 'bar', area: false };
}

/**
 * Build the EChartsOption for a unified bar/column chart from flat rows.
 * Returns an option with empty series when the config is incomplete (no category
 * or no measures) so the caller can still render an (empty) chart safely.
 */
export function buildBarColumnOption(rows: Array<Record<string, unknown>>, capabilities: BarColumnCapabilities): EChartsOption {
  const safeRows = Array.isArray(rows) ? rows : [];
  const horizontal = capabilities.orientation === 'horizontal';
  // Render dispatch is driven by CONFIG flags (variantFlags), not variant-name
  // strings — so a new variant is added purely in JSON. Fall back to the legacy
  // name checks only when flags are absent (older saved charts).
  const vf = capabilities.variantFlags;
  const isHundred = vf ? vf.normalize100 === true : capabilities.variant === 'stacked-100';
  const isStacked = vf ? (vf.stack === true || isHundred) : (capabilities.variant === 'stacked' || capabilities.variant === 'stacked-100');
  const isComparison = vf ? vf.comparison === true : capabilities.variant === 'comparison';
  const agg: Agg = capabilities.aggregation || 'sum';
  const showValues = capabilities.showValues !== false;
  const measures = (capabilities.measures || []).filter((m) => m.field);
  // Measure display name for legends / series / axis names — Capital Case per
  // UI standards, so raw column keys (net_sales, ProfitPerOrder) never leak
  // into the chart chrome. Data VALUES (categories, series values) stay as-is.
  const displayName = (m: BarColumnMeasure): string => toCapitalCase(m.label || m.field);

  // Config-driven styling defaults (from the builder's `barChartDefaults`). Each
  // falls back to a built-in constant only when the config omits it, so nothing is
  // truly hardcoded when the config provides it.
  const d = capabilities.chartDefaults || {};
  const cfgPalette = d.palette?.length ? d.palette : DEFAULT_PALETTE;
  const defaultBarWidth = d.barWidthPercent ?? 60;
  const valueAxisTicks = d.valueAxisTicks ?? 4;
  const labelMinMargin = d.labelMinMargin ?? 4;
  const catLabelWidthV = d.categoryLabelWidthVertical ?? 84;
  const catLabelWidthH = d.categoryLabelWidthHorizontal ?? 96;
  const refLineColor = d.referenceLineColor || '#64748b';
  const refLineStyle = d.referenceLineStyle || 'dashed';
  const cfgVarianceUp = d.varianceUpColor || '#10b981';
  const cfgVarianceDown = d.varianceDownColor || '#ef4444';
  const varianceLabelColor = d.varianceLabelColor || '#334155';
  const rotateThreshold = d.rotateThreshold ?? 6;
  const labelPosVertical = d.labelPositionVertical || 'top';
  const labelPosHorizontal = d.labelPositionHorizontal || 'right';
  // Pixel-level layout constants (all config-driven; number() coerces + falls back).
  const L = (d.layout || {}) as Record<string, number | undefined>;
  const lx = (key: string, fallback: number): number => (typeof L[key] === 'number' ? (L[key] as number) : fallback);
  const gridLeft = lx('gridLeft', 12);
  const gridRightBase = lx('gridRightBase', 16);
  const gridRightLegend = lx('gridRightLegend', 72);
  const gridTopBase = lx('gridTopBase', 28);
  const gridTopLegend = lx('gridTopLegend', 36);
  const gridBottomBase = lx('gridBottomBase', 16);
  const gridBottomLegend = lx('gridBottomLegend', 30);
  const gridBottomAxisName = lx('gridBottomAxisName', 30);
  const gridHierarchyBottom = lx('gridHierarchyBottom', 24);
  const gridHierarchyLeft = lx('gridHierarchyLeft', 40);
  const catNameGapV = lx('categoryNameGapVertical', 30);
  const catNameGapHOffset = lx('categoryNameGapHorizontalOffset', 18);
  const catNamePadTop = lx('categoryNamePaddingTop', 8);
  const valNameGapV = lx('valueNameGapVertical', 50);
  const valNameGapH = lx('valueNameGapHorizontal', 34);
  // The vertical value-axis NAME floats at the TOP of the axis (nameLocation
  // 'end') rather than rotated in the middle. ECharts' `containLabel` reserves
  // room for axis LABELS but NOT a middle/rotated name, so a middle name either
  // overlaps wide labels (e.g. "17,605,037") or clips off the left edge when the
  // labels are short (e.g. "17.6M"). Anchoring the name to the top makes it
  // label-width-INDEPENDENT — it never overlaps and never disappears when the
  // number scale changes. Config can override via layout.valueNameLocationVertical.
  const valNameLocV = (typeof L['valueNameLocationVertical'] === 'string' ? (L['valueNameLocationVertical'] as unknown as string) : 'end');
  const valNameGapEndV = lx('valueNameGapEndVertical', 8);
  const zoomSliderPadding = lx('zoomSliderPadding', 30);
  const legendRightOffset = lx('legendRightOffset', 4);

  // Color precedence. A chart that needs MANY colors (series split, multiple
  // measures, or comparison) is driven by the PALETTE. A genuinely single-colour
  // chart (one measure, no split) uses the explicit single "bar colour" when set.
  const needsManyColors =
    !!capabilities.series || measures.length > 1 || isComparison;
  const paletteColors = capabilities.paletteColors?.length ? capabilities.paletteColors : cfgPalette;
  const palette = needsManyColors
    ? paletteColors
    : (capabilities.colors?.length ? capabilities.colors : paletteColors);

  const category = capabilities.category;
  if (!category || !measures.length) {
    return { series: [] };
  }

  // Category order: distinct rows, then optional sort-by-value + Top-N (Advanced).
  // Sort/limit key = each category's aggregated FIRST-measure value.
  let categories = distinct(safeRows, category);
  const sortBy = capabilities.sortBy || 'none';
  const topN = Math.max(0, Math.floor(capabilities.topN ?? 0));
  if (sortBy === 'asc' || sortBy === 'desc' || topN > 0) {
    const rankVals = aggregateByCategory(safeRows, category, measures[0].field, categories, agg);
    let pairs = categories.map((cat, i) => ({ cat, v: rankVals[i] }));
    if (sortBy === 'asc') pairs = pairs.sort((a, b) => a.v - b.v);
    else if (sortBy === 'desc') pairs = pairs.sort((a, b) => b.v - a.v);
    else pairs = pairs.sort((a, b) => b.v - a.v); // Top-N with no sort → rank by value
    if (topN > 0) pairs = pairs.slice(0, topN);
    categories = pairs.map((p) => p.cat);
    // A horizontal bar's category axis is ECharts' Y axis, which places
    // data[0] at the BOTTOM and data[last] at the TOP by default — so hanging
    // the "biggest-first" sorted array straight off the array put the biggest
    // bar at the bottom, backwards from how a ranked list reads (biggest on
    // top). Reverse just the display order to compensate; the vertical/column
    // case uses the X axis, which reads data[0] first left-to-right and needs
    // no such flip.
    if (horizontal) categories = [...categories].reverse();
  }

  // Hierarchical Category Axis (parent → child): when a parent column is set,
  // reorder the child categories so all children of a parent are contiguous, and
  // compute the parent groups (each with its label + how many children it spans)
  // so a second "band" axis can be drawn beneath the child labels.
  const parentField = capabilities.categoryParent;
  let parentGroups: Array<{ label: string; span: number }> = [];
  if (parentField && parentField !== category) {
    const childToParent = new Map<string, string>();
    safeRows.forEach((row) => {
      const child = String(row[category] ?? '');
      if (!childToParent.has(child)) childToParent.set(child, String(row[parentField] ?? ''));
    });
    // Stable parent order (first appearance), children kept in current order.
    const parentOrder: string[] = [];
    const byParent = new Map<string, string[]>();
    categories.forEach((child) => {
      const parent = childToParent.get(child) ?? '';
      if (!byParent.has(parent)) { byParent.set(parent, []); parentOrder.push(parent); }
      byParent.get(parent)!.push(child);
    });
    categories = parentOrder.flatMap((p) => byParent.get(p)!);
    parentGroups = parentOrder.map((p) => ({ label: p, span: byParent.get(p)!.length }));
  }

  const dualAxis = measures.some((m) => (m.axis || 'y1') === 'y2');
  const refMarkLine = referenceMarkLine(capabilities.referenceLines, horizontal, refLineColor, refLineStyle);

  // Format-popup + Advanced overrides.
  const barWidth = Math.max(10, Math.min(100, capabilities.barWidthPercent ?? defaultBarWidth));
  const smooth = capabilities.smoothLines === true;
  const showGrid = capabilities.showGridLines !== false;
  const labelMode = capabilities.labelMode || 'value';
  const barCornerRadius = Math.max(0, capabilities.barCornerRadius ?? 0);
  const barGap = capabilities.barGapPercent !== undefined ? `${capabilities.barGapPercent}%` : undefined;
  const barCategoryGap = capabilities.barCategoryGapPercent !== undefined ? `${capabilities.barCategoryGapPercent}%` : undefined;
  const showStackTotals = capabilities.showStackTotals === true && isStacked;
  // Rounded-corner itemStyle for bars (skips the round on inner stacked segments
  // is not attempted — a single radius reads fine for both).
  const barItemStyle = (color: string): Record<string, unknown> =>
    barCornerRadius > 0 ? { color, borderRadius: barCornerRadius } : { color };

  const seriesList: Array<Record<string, unknown>> = [];
  const legendNames: string[] = [];
  // Set (below, horizontal-stack-totals only) to the value the invisible total
  // track spans to — also forced onto the value axis's own `max` so the track
  // ends EXACTLY at the plot's right edge, not just at "the longest bar's own
  // total" (which left a gap between the totals column and the true edge).
  let totalTrackMax: number | undefined;

  const labelPosition = horizontal ? labelPosHorizontal : labelPosVertical;
  // A vertical STACKED bar's label positioned 'top' (the usual default, right
  // for a single/grouped bar) sits ABOVE whichever segment owns it — for the
  // segment on top of the stack that's the open whitespace above the whole
  // bar, floating disconnected from any color. Pin it inside the segment's own
  // top edge instead so every value reads as sitting ON its bar. Horizontal
  // stacks keep their existing outside-right placement (a different, already
  // working layout — see the stack-totals track above).
  const segmentLabelPosition = !horizontal && isStacked ? 'insideTop' : labelPosition;
  // Data-label content per `labelMode` (mirrors the chart-builder). Percent is
  // relative to this series' own total; name-* prefixes the series/point name.
  // hideOverlap + minMargin + zero-suppression stop the value labels from piling
  // up into an unreadable wall when there are many bars/series per category.
  const makeLabel = (seriesName: string, seriesTotal: number): Record<string, unknown> => ({
    show: showValues,
    position: segmentLabelPosition,
    hideOverlap: true,
    minMargin: labelMinMargin,
    formatter: (params: { value: number; name?: string }) => {
      const value = Number(params.value);
      // Don't label empty/zero segments — they add clutter, not information.
      if (!value) return '';
      const pct = seriesTotal ? (value / seriesTotal) * 100 : 0;
      switch (labelMode) {
        case 'percent': return `${pct.toFixed(capabilities.decimals ?? 1)}%`;
        case 'name-value': return `${params.name ?? seriesName}: ${formatValue(value, capabilities)}`;
        case 'name-percent': return `${params.name ?? seriesName}: ${pct.toFixed(capabilities.decimals ?? 1)}%`;
        case 'value':
        default: return formatValue(value, capabilities);
      }
    },
  });
  const sumOf = (values: number[]): number => values.reduce((a, b) => a + (Number(b) || 0), 0);

  if (isComparison) {
    // Comparison Bar — Primary (measure 0) vs Comparison (measure 1) as two bars
    // side-by-side per category, with an optional per-category variance % label
    // (primary vs comparison), coloured favourable/unfavourable.
    const primary = measures[0];
    const comparison = measures[1] || measures[0];
    const primaryVals = aggregateByCategory(safeRows, category, primary.field, categories, agg);
    const comparisonVals = aggregateByCategory(safeRows, category, comparison.field, categories, agg);
    const upColor = capabilities.varianceUpColor || cfgVarianceUp;
    const downColor = capabilities.varianceDownColor || cfgVarianceDown;
    const showVariance = capabilities.showVariance !== false;

    legendNames.push(displayName(primary), displayName(comparison));
    seriesList.push({
      name: displayName(primary),
      type: 'bar',
      barWidth: `${barWidth}%`,
      ...(barGap !== undefined ? { barGap } : {}),
      emphasis: { focus: 'series' },
      itemStyle: barItemStyle(palette[0]),
      // Variance % label sits on the PRIMARY bar (over/under vs comparison).
      label: showVariance
        ? {
            show: true,
            position: labelPosition,
            formatter: (p: { dataIndex: number }) => {
              const base = comparisonVals[p.dataIndex] || 0;
              const pv = primaryVals[p.dataIndex] || 0;
              if (!base) return formatValue(pv, capabilities);
              const pct = ((pv - base) / Math.abs(base)) * 100;
              return `${pct >= 0 ? '+' : ''}${pct.toFixed(capabilities.decimals ?? 1)}%`;
            },
            color: varianceLabelColor,
          }
        : makeLabel(displayName(primary), sumOf(primaryVals)),
      data: primaryVals.map((v, i) => ({
        value: v,
        // Tint the primary bar by whether it beats the comparison.
        itemStyle: barItemStyle((v >= (comparisonVals[i] || 0)) ? upColor : downColor),
      })),
      ...(refMarkLine ? { markLine: refMarkLine } : {}),
    });
    seriesList.push({
      name: displayName(comparison),
      type: 'bar',
      barWidth: `${barWidth}%`,
      ...(barGap !== undefined ? { barGap } : {}),
      emphasis: { focus: 'series' },
      itemStyle: barItemStyle(palette[1 % palette.length]),
      label: makeLabel(displayName(comparison), sumOf(comparisonVals)),
      data: comparisonVals,
    });
  } else if (capabilities.series) {
    // Split by a series column: grouped / stacked / 100% / clustered. The FIRST
    // BAR-type measure supplies the split value. Any ADDITIONAL measure marked
    // "Draw as = Line/Area" is overlaid as a combo line on top (Line + Grouped
    // Column / Line + Stacked Column), optionally on the secondary axis.
    const barMeasure = measures.find((m) => (m.renderAs || 'bar') === 'bar') || measures[0];
    const overlayMeasures = measures.filter((m) => m !== barMeasure && (m.renderAs || 'bar') !== 'bar');
    const valueField = barMeasure.field;
    const seriesNames = orderConfidenceTiers(distinct(safeRows, capabilities.series));
    const grid = aggregateByCategoryAndSeries(safeRows, category, valueField, capabilities.series, categories, seriesNames, agg);
    let grids = seriesNames.map((name) => grid.get(name) || []);
    if (isHundred) grids = normalizeTo100(grids);

    // Per-category stack totals (label on the LAST series so it sits on top).
    // Computed whenever the chart is stacked — showStackTotals uses it to
    // REPLACE segment labels; the vertical grand-total overlay below (added
    // when showStackTotals is off) uses it to show a total ON TOP OF the
    // segment labels instead, so both can be visible together.
    const stackColTotals = isStacked
      ? categories.map((_, ci) => grids.reduce((s, g) => s + (Number(g[ci]) || 0), 0))
      : [];
    // A horizontal chart's bars all start at 0 but END at different lengths, so
    // a total label attached to each bar's OWN end (position 'right') lands at a
    // different X per category — a ragged column, not a lined-up right-hand
    // total. Fix: an invisible bar spanning every category out to the SAME
    // fixed value (the largest total) sits fully behind the real stack
    // (barGap:'-100%', transparent, silent) and carries the total's label at
    // its OWN end instead — which is identical for every category, giving one
    // straight right-aligned column. Vertical bars keep the simpler
    // top-of-stack label (their "total" already reads fine at the bar's peak).
    // Pinned to the value axis's own max (below) — an explicit valueAxisMax
    // wins if the user set one, else a "nice" ceiling just above the largest
    // total, so the axis itself reaches exactly where the track (and its
    // label) ends, with no leftover gap.
    if (showStackTotals && horizontal) {
      totalTrackMax = capabilities.valueAxisMax ?? niceCeil(Math.max(...stackColTotals, 1));
    }
    seriesNames.forEach((name, index) => {
      legendNames.push(name);
      const isTopOfStack = showStackTotals && index === seriesNames.length - 1;
      // "Show stack totals" REPLACES every segment's own value label with ONE
      // total (rendered on the invisible track series for horizontal, or on
      // the top segment for vertical) — showing both (each segment's value AND
      // the total) was cluttered and made the total read like just another
      // segment. Non-top segments get no label at all in this mode.
      const label = isTopOfStack && !horizontal
        ? {
            show: true,
            position: segmentLabelPosition,
            formatter: (p: { dataIndex: number }) => formatValue(stackColTotals[p.dataIndex] || 0, capabilities),
          }
        : showStackTotals
          ? { show: false }
          : makeLabel(name, sumOf(grids[index]));
      seriesList.push({
        name,
        id: name,
        type: 'bar',
        ...(isStacked ? { stack: 'total' } : {}),
        barWidth: `${barWidth}%`,
        ...(barGap !== undefined ? { barGap } : {}),
        ...(barCategoryGap !== undefined ? { barCategoryGap } : {}),
        emphasis: { focus: 'series' },
        itemStyle: barItemStyle(palette[index % palette.length]),
        label,
        data: grids[index],
        ...(index === 0 && refMarkLine ? { markLine: refMarkLine } : {}),
      });
    });
    if (totalTrackMax) {
      const trackMax = totalTrackMax;
      seriesList.push({
        name: '__stackTotalTrack',
        type: 'bar',
        stack: '__stackTotalTrack',
        barWidth: `${barWidth}%`,
        barGap: '-100%',
        silent: true,
        itemStyle: { color: 'transparent' },
        z: -1,
        label: {
          show: true,
          position: 'right',
          formatter: (p: { dataIndex: number }) => formatValue(stackColTotals[p.dataIndex] || 0, capabilities),
        },
        data: categories.map(() => trackMax),
      });
    }
    // Vertical grand-total overlay: an extra zero-height series stacked ON TOP
    // of the real segments, invisible itself, carrying just the bar's total
    // ABOVE the whole stack — additive, so segment labels stay put underneath
    // it. Only when showStackTotals is off (that toggle already shows a total,
    // just by REPLACING segment labels instead — this avoids showing the total
    // twice) and there's more than one segment (a single-segment bar's own
    // label already reads as the total).
    if (isStacked && !horizontal && !showStackTotals && seriesNames.length > 1) {
      seriesList.push({
        name: '__stackGrandTotal',
        type: 'bar',
        stack: 'total',
        barWidth: `${barWidth}%`,
        silent: true,
        itemStyle: { color: 'transparent' },
        label: {
          show: true,
          position: 'top',
          distance: 8,
          fontWeight: 700,
          formatter: (p: { dataIndex: number }) => formatValue(stackColTotals[p.dataIndex] || 0, capabilities),
        },
        data: categories.map(() => 0),
      });
    }

    // Combo overlay line(s): each extra Line/Area measure across the categories.
    overlayMeasures.forEach((measure, oi) => {
      const label = displayName(measure);
      legendNames.push(label);
      const values = aggregateByCategory(safeRows, category, measure.field, categories, agg);
      const onSecondary = (measure.axis || 'y1') === 'y2';
      const color = palette[(seriesNames.length + oi) % palette.length];
      seriesList.push({
        name: label,
        type: 'line',
        smooth,
        ...(measure.renderAs === 'area' ? { areaStyle: {} } : {}),
        ...(onSecondary && dualAxis ? (horizontal ? { xAxisIndex: 1 } : { yAxisIndex: 1 }) : {}),
        emphasis: { focus: 'series' },
        itemStyle: { color },
        lineStyle: { color },
        label: makeLabel(label, sumOf(values)),
        data: values,
      });
    });
  } else {
    // No series split — one series per measure (Multiple Measures / combos).
    // A SINGLE-measure bar uses ONE solid colour for every bar (the palette's
    // first colour) — the data-viz standard: one measure ⇒ one colour, since the
    // X-axis labels already distinguish the bars, so per-bar colours would only
    // imply a meaning that isn't there. Palette change still applies (palette[0]
    // moves with the chosen palette). Multi-measure keeps one colour per measure.
    // 100%-stacked normalises ACROSS measures here (they are the series). Only
    // bar measures participate — a line/area overlay keeps its real values.
    const rawValues = measures.map((measure) => aggregateByCategory(safeRows, category, measure.field, categories, agg));
    let measureValues = rawValues;
    if (isHundred) {
      const stackedIdx = measures
        .map((measure, i) => ({ measure, i }))
        .filter(({ measure }) => seriesTypeFor(measure).type === 'bar')
        .map(({ i }) => i);
      const normalized = normalizeTo100(stackedIdx.map((i) => rawValues[i]));
      measureValues = rawValues.map((values, i) => {
        const slot = stackedIdx.indexOf(i);
        return slot === -1 ? values : normalized[slot];
      });
    }

    measures.forEach((measure, index) => {
      const label = displayName(measure);
      legendNames.push(label);
      const { type, area } = seriesTypeFor(measure);
      const values = measureValues[index];
      const onSecondary = (measure.axis || 'y1') === 'y2';
      seriesList.push({
        name: label,
        id: label,
        type,
        ...(type === 'bar' ? { barWidth: `${barWidth}%` } : {}),
        ...(type === 'bar' && barGap !== undefined ? { barGap } : {}),
        ...(type === 'bar' && barCategoryGap !== undefined ? { barCategoryGap } : {}),
        ...(type === 'line' ? { smooth } : {}),
        ...(isStacked && type === 'bar' ? { stack: 'total' } : {}),
        ...(area ? { areaStyle: {} } : {}),
        // Secondary axis index differs by orientation (dual-Y for vertical,
        // dual-X for horizontal).
        ...(onSecondary && dualAxis ? (horizontal ? { xAxisIndex: 1 } : { yAxisIndex: 1 }) : {}),
        emphasis: { focus: 'series' },
        // One colour per measure/series (single-measure ⇒ every bar palette[0]).
        itemStyle: type === 'bar'
          ? barItemStyle(palette[index % palette.length])
          : { color: palette[index % palette.length] },
        lineStyle: type === 'line' ? { color: palette[index % palette.length] } : undefined,
        label: makeLabel(label, sumOf(values)),
        data: values,
        ...(index === 0 && refMarkLine ? { markLine: refMarkLine } : {}),
      });
    });
  }

  // Category-axis label rotation: explicit override ('auto' → heuristic).
  const rotationSetting = capabilities.axisLabelRotation || 'auto';
  const catRotate = rotationSetting === 'auto'
    ? (categories.length > rotateThreshold ? 30 : 0)
    : Number(rotationSetting) || 0;

  // When the user hasn't typed an explicit axis title, fall back to the MEASURE
  // name so the value axes are never anonymous (matches what the series legend /
  // dropdown shows). Primary axis → the first non-secondary (bar) measure; the
  // secondary (dual) axis → the measure bound to y2.
  const measureName = (m?: BarColumnMeasure) => (m ? displayName(m) : '');
  const primaryValMeasure = measures.find((m) => (m.axis || 'y1') !== 'y2') || measures[0];
  const secondaryValMeasure = measures.find((m) => (m.axis || 'y1') === 'y2');
  const valAxisFallback = measureName(primaryValMeasure);
  const valAxis2Fallback = measureName(secondaryValMeasure);

  // Right-hand room for HORIZONTAL data labels.
  //
  // A horizontal bar labels its value OUTSIDE the bar end (position 'right'),
  // but ECharts' `containLabel` only reserves space for AXIS labels — a series
  // label is drawn outside the grid and simply gets clipped by the chart edge.
  // With the longest bar reaching the axis maximum its label always sits hard
  // against the right border, which is why "6M" rendered as "6I". Measure the
  // widest label the formatter can produce and reserve that many pixels.
  // Stack totals ALWAYS render outside-right regardless of the "Show data
  // labels" (showValues) toggle — someone turning per-segment labels off
  // while keeping "Show stack totals" on still needs this space reserved,
  // or the total (and the scrollbar pushed past it) collapse back together.
  const labelsOutsideRight =
    horizontal && (labelPosition === 'right' || labelPosition === 'outside') && (showValues || showStackTotals);
  const labelCharPx = lx('labelCharPx', 7);
  const labelEdgeGapPx = lx('labelEdgeGapPx', 8);
  const widestLabelChars = labelsOutsideRight
    ? seriesList.reduce((widest, s) => {
        const seriesName = String(s['name'] ?? '');
        const points = Array.isArray(s['data']) ? (s['data'] as unknown[]) : [];
        return points.reduce((max: number, point) => {
          const raw = point && typeof point === 'object' ? (point as { value?: unknown }).value : point;
          const value = Number(raw);
          // Zero/blank segments are not labelled (see makeLabel), so they
          // reserve nothing.
          if (!Number.isFinite(value) || !value) return max;
          const decimals = capabilities.decimals ?? 1;
          const pct = `${(100).toFixed(decimals)}%`;
          const text =
            labelMode === 'percent' ? pct
            : labelMode === 'name-value' ? `${seriesName}: ${formatValue(value, capabilities)}`
            : labelMode === 'name-percent' ? `${seriesName}: ${pct}`
            : formatValue(value, capabilities);
          return Math.max(max, text.length);
        }, widest);
      }, 0)
    : 0;
  // A stack-total's text (often currency-prefixed, e.g. "$150M") consistently
  // rendered WIDER than this per-character estimate accounted for, letting the
  // real label creep into the reserved buffer the auto scrollbar sits in —
  // pad the estimate itself rather than just nudging the scrollbar over.
  const outsideLabelRightPad = widestLabelChars
    ? Math.round(widestLabelChars * labelCharPx) + labelEdgeGapPx + (showStackTotals ? 16 : 0)
    : 0;

  // Axis titles are keyed by PHYSICAL position (X = horizontal, Y = vertical),
  // mirroring the chart-builder. The category axis is X when vertical and Y when
  // horizontal; the value axis is the opposite. Secondary (dual) titles bind to
  // whichever physical axis carries the second value scale.
  const catAxisLabel = horizontal ? capabilities.yAxisLabel : capabilities.xAxisLabel;
  const valAxisLabel = (horizontal ? capabilities.xAxisLabel : capabilities.yAxisLabel) || valAxisFallback;
  const valAxis2Label = (horizontal ? capabilities.xAxisLabel2 : capabilities.yAxisLabel2) || valAxis2Fallback;

  // Axis definitions. `catAxis` holds the (child) categories; `valAxis` is numeric.
  // Category labels can be long (timestamps, codes) and collide on narrow tiles —
  // truncate with ellipsis, hide overlapping labels, and let ECharts thin the
  // tick interval automatically (interval: 'auto') instead of forcing every one.
  // Category-axis label width caps so long codes truncate with an ellipsis
  // instead of colliding, and the axis NAME sits clear beyond them.
  const catLabelWidth = horizontal ? catLabelWidthH : catLabelWidthV;
  // HORIZONTAL: the category axis is on the LEFT (Y). Its name at nameLocation
  // 'middle' sits at a fixed gap LEFT of the labels — but `containLabel` doesn't
  // reserve room for it, so it clips off the left edge and the title disappears
  // (same failure as the value-axis name). Float it at the TOP ('end') instead,
  // label-width-independent. VERTICAL keeps the centered name below the labels
  // (that already works, with enough bottom band reserved). Config override:
  // layout.categoryNameLocationHorizontal.
  const catNameLocH = (typeof L['categoryNameLocationHorizontal'] === 'string' ? (L['categoryNameLocationHorizontal'] as unknown as string) : 'end');
  const catNameIsEndH = horizontal && catNameLocH === 'end';
  const catNameGapEndH = lx('categoryNameGapEndHorizontal', 8);
  const catAxis: Record<string, unknown> = {
    type: 'category',
    data: categories,
    name: toCapitalCase(catAxisLabel || capabilities.categoryLabel || ''),
    nameLocation: catNameIsEndH ? 'end' : 'middle',
    // Push the axis name past the (truncated) labels so they never overlap.
    nameGap: catNameIsEndH ? catNameGapEndH : (horizontal ? catLabelWidth + catNameGapHOffset : catNameGapV),
    nameTextStyle: catNameIsEndH
      ? { align: 'left' as const }
      : { padding: horizontal ? [0, 0, 0, 0] : [catNamePadTop, 0, 0, 0] },
    axisLabel: horizontal
      ? { hideOverlap: true, overflow: 'truncate', width: catLabelWidth }
      : { rotate: catRotate, interval: 'auto', hideOverlap: true, overflow: 'truncate', width: catLabelWidth },
  };

  // Hierarchical Category Axis — a second, outer category axis aligned 1:1 with
  // the child categories, but whose label is shown only at each group's CENTRE
  // child (blank elsewhere), so the parent name reads as a band under its
  // children. Group boundaries are drawn with the axis ticks.
  const hasHierarchy = parentGroups.length > 0;
  const parentLabelByIndex: string[] = [];
  if (hasHierarchy) {
    let i = 0;
    parentGroups.forEach((g) => {
      const centre = i + Math.floor((g.span - 1) / 2);
      for (let k = 0; k < g.span; k++) parentLabelByIndex[i + k] = i + k === centre ? g.label : '';
      i += g.span;
    });
  }
  const parentBandAxis: Record<string, unknown> | undefined = hasHierarchy
    ? {
        type: 'category',
        data: categories, // 1:1 with the child axis so positions line up
        position: horizontal ? 'left' : 'bottom',
        offset: horizontal ? 60 : 30,
        axisLabel: {
          interval: 0,
          fontWeight: 'bold' as const,
          formatter: (_v: string, index: number) => parentLabelByIndex[index] ?? '',
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLine: { show: false },
      }
    : undefined;
  // Value-axis Advanced overrides: log scale + manual min/max. Log can't be used
  // with 100%-stacked (fixed 0–100) so it's ignored there.
  const useLog = capabilities.valueAxisLog === true && !isHundred;
  // Vertical value axis → name floats at the TOP (nameLocation 'end'), left-
  // aligned above the axis, so it's independent of label width. Horizontal value
  // axis (value on X) keeps the centered name below, which already works.
  const valNameIsEnd = !horizontal && valNameLocV === 'end';
  const valAxisBase: Record<string, unknown> = {
    type: useLog ? 'log' : 'value',
    name: toCapitalCase(valAxisLabel || ''),
    nameLocation: valNameIsEnd ? 'end' : 'middle',
    nameGap: valNameIsEnd ? valNameGapEndV : (horizontal ? valNameGapH : valNameGapV),
    ...(valNameIsEnd ? { nameTextStyle: { align: 'left' as const } } : {}),
    // Fewer ticks so the value labels don't cram/overlap on a narrow tile.
    splitNumber: valueAxisTicks,
    splitLine: { show: showGrid },
    ...(capabilities.valueAxisMin !== undefined && !isHundred ? { min: capabilities.valueAxisMin } : {}),
    // The right-aligned stack-total column (built above) needs the axis's
    // actual max to equal totalTrackMax exactly, or its track — and the
    // total labels riding on it — stops short of the true right edge.
    ...(totalTrackMax !== undefined && !isHundred ? { max: totalTrackMax }
      : capabilities.valueAxisMax !== undefined && !isHundred ? { max: capabilities.valueAxisMax } : {}),
    ...(isHundred
      ? { max: 100, axisLabel: { formatter: '{value}%', hideOverlap: true } }
      : { axisLabel: { formatter: (value: number) => formatValue(value, capabilities), hideOverlap: true } }),
  };
  const valAxes: Record<string, unknown>[] = dualAxis
    ? [valAxisBase, { ...valAxisBase, name: toCapitalCase(valAxis2Label || ''), position: horizontal ? 'top' : 'right', splitLine: { show: false } }]
    : [valAxisBase];

  // Tooltip — full ChartStyle-equivalent (enable, trigger, axis pointer, colors,
  // radius, font). `tooltipEnabled: false` or trigger 'none' hides it.
  const tooltipTrigger = capabilities.tooltipTrigger || 'axis';
  const tooltipOff = capabilities.tooltipEnabled === false || tooltipTrigger === 'none';
  const resolvedTrigger = tooltipTrigger === 'auto' ? 'axis' : tooltipTrigger;
  const tooltipPos = tooltipPositionFor(capabilities.tooltipPosition);
  // A mini-chart tooltip needs the per-category series list, so it forces the
  // 'axis' trigger (item trigger only carries one point).
  const wantMiniChart = capabilities.tooltipShowChart === true;
  const tooltip = tooltipOff
    ? { show: false }
    : {
        show: true,
        trigger: wantMiniChart ? 'axis' : resolvedTrigger,
        axisPointer: { type: capabilities.tooltipAxisPointer && capabilities.tooltipAxisPointer !== 'none' ? capabilities.tooltipAxisPointer : 'shadow' },
        ...(tooltipPos !== undefined ? { position: tooltipPos } : {}),
        ...(wantMiniChart ? { formatter: miniChartTooltipFormatter((d.tooltip || {}) as Record<string, string | number | undefined>, (v: number) => formatValue(v, capabilities)) } : {}),
        ...(capabilities.tooltipBackground ? { backgroundColor: capabilities.tooltipBackground } : {}),
        ...(capabilities.tooltipBorderColor ? { borderColor: capabilities.tooltipBorderColor } : {}),
        ...(capabilities.tooltipBorderRadius !== undefined ? { borderRadius: capabilities.tooltipBorderRadius } : {}),
        // The mini-chart HTML sets its own text colors, but honor the text color
        // for the title/values when provided.
        textStyle: {
          ...(capabilities.tooltipTextColor ? { color: capabilities.tooltipTextColor } : {}),
          ...(capabilities.tooltipFontSize ? { fontSize: capabilities.tooltipFontSize } : {}),
        },
      };

  // Zoom / pan (Interaction) — a dataZoom on the category and/or value axis.
  // Category axis is index 0 on X (vertical) or Y (horizontal); value is the other.
  const catOnX = !horizontal;
  // Auto scrollbar: independent of the manual "Enable zoom" toggle — a category
  // axis with more than 5 entries gets a plain slider (bottom for a vertical/
  // column chart, right for a horizontal one) so long lists (years, regions,
  // customers, ...) stay readable without the tile growing unbounded. Skipped
  // when the manual zoom toggle is already on, so the two features never both
  // add a slider to the same axis.
  const autoCategoryScroll = capabilities.enableZoom !== true && categories.length > 5;
  // Plain-scrollbar look (flat thumb on a light track) instead of ECharts'
  // default zoom slider (wavy data-preview shape + drag handles + labels) —
  // this is a "there's more, scroll for it" affordance, not a zoom tool.
  const plainScrollStyle = {
    showDataShadow: false,
    showDetail: false,
    brushSelect: false,
    handleSize: 0,
    moveHandleSize: 0,
    fillerColor: '#c1c1c1',
    borderColor: 'transparent',
    backgroundColor: '#f1f1f1',
  };
  const dataZoom = ((): Array<Record<string, unknown>> | undefined => {
    if (autoCategoryScroll) {
      // Thumb width/height must reflect how much of the list is actually
      // visible (5 of N) — omitting start/end defaults to the full 0-100
      // range, which draws a thumb spanning the WHOLE track (looks like one
      // solid bar, not a scrollbar).
      const visiblePct = Math.min(100, (5 / categories.length) * 100);
      // A horizontal chart's category scrollbar sits on the right — same edge
      // the stack-total column occupies (see outsideLabelRightPad) — so push
      // it OUT past that reserved label width instead of pinning it to the
      // container's true edge, or the two visually collide.
      // Pinned to the container's true right edge (the extreme end) — the
      // stack-total column sits INSIDE the reserved outsideLabelRightPad
      // buffer to its left, which now pads generously enough to not reach it.
      const catScrollRight = 0;
      const zooms: Array<Record<string, unknown>> = [{
        type: 'slider',
        ...plainScrollStyle,
        start: 0,
        end: visiblePct,
        ...(catOnX ? { xAxisIndex: 0, bottom: 0, height: 8 } : { yAxisIndex: 0, right: catScrollRight, width: 8 }),
      }];
      // A vertical/column chart's category scrollbar sits at the bottom, so
      // its "opposite side" companion (matching the horizontal chart's single
      // right-side bar) is a right-side scrollbar on the VALUE axis instead.
      if (!horizontal) {
        zooms.push({ type: 'slider', ...plainScrollStyle, yAxisIndex: 0, right: 0, width: 8 });
      }
      return zooms;
    }
    if (capabilities.enableZoom !== true) return undefined;
    const which = capabilities.zoomAxis || 'category';
    const showSlider = capabilities.showZoomSlider !== false;
    const zooms: Array<Record<string, unknown>> = [];
    const addZoom = (onX: boolean) => {
      zooms.push({ type: 'inside', ...(onX ? { xAxisIndex: 0 } : { yAxisIndex: 0 }) });
      if (showSlider) zooms.push({ type: 'slider', ...(onX ? { xAxisIndex: 0, bottom: 0 } : { yAxisIndex: 0, right: 0 }) });
    };
    if (which === 'category' || which === 'both') addZoom(catOnX);
    if (which === 'value' || which === 'both') addZoom(!catOnX);
    return zooms.length ? zooms : undefined;
  })();

  // Legend — honor the explicit showLegend toggle + placement (Formatting/Legend).
  // The toggle is authoritative: if the user turns Legend ON it shows even for a
  // single series (a single-measure bar's legend lists that one measure). Only a
  // multi-series/measure chart auto-shows it; the toggle can always override.
  const legendPos = capabilities.legendPosition || 'bottom';
  const showLegend = capabilities.showLegend !== false && legendPos !== 'none' && legendNames.length > 0;
  // A bottom legend must sit CLEAR of the category-axis name (which also renders
  // at the bottom) — pin it to bottom:0 and reserve enough grid.bottom for both.
  const bottomLegend = showLegend && legendPos === 'bottom';
  // Smaller than ECharts' own defaults (25×14 swatch, ~12px text) — those read
  // oversized on a compact dashboard tile. "Legend font size" (Format popup)
  // scales both the swatch and the text together so they stay proportional.
  const legendFontSize = capabilities.legendFontSize ?? 9;
  const legend = showLegend
    ? {
        data: legendNames,
        type: 'scroll' as const,
        itemWidth: Math.round(legendFontSize * 1.3),
        itemHeight: Math.round(legendFontSize * 0.9),
        itemGap: Math.round(legendFontSize * 0.9),
        textStyle: { fontSize: legendFontSize },
        // Top/bottom legends default to horizontally CENTERED — right-align
        // them instead (still on their own top/bottom row, just pushed to the
        // tile's right edge) so they don't compete for the same visual weight
        // as the title. The dedicated 'right' position is unaffected — it's
        // already a vertical stack pinned to the right edge.
        ...(legendPos === 'top' ? { top: 0, left: 'right' as const }
          : legendPos === 'right' ? { right: legendRightOffset, orient: 'vertical' as const, top: 'middle' as const }
          : { bottom: 0, left: 'right' as const }),
      }
    : undefined;

  // Reserve extra room when a zoom slider is shown so it doesn't cover the plot.
  const sliderShown = !!dataZoom && capabilities.showZoomSlider !== false;
  // The auto scrollbar(s) always need their own reservation regardless of the
  // manual zoomAxis setting — a vertical chart gets BOTH a bottom (category)
  // and right (value) bar, a horizontal chart just the right (category) one.
  const zoomBottom = autoCategoryScroll
    ? (!horizontal ? zoomSliderPadding : 0)
    : (sliderShown && (capabilities.zoomAxis || 'category') !== 'value' && !horizontal ? zoomSliderPadding : 0);
  const zoomRight = autoCategoryScroll
    ? zoomSliderPadding
    : (sliderShown && ((capabilities.zoomAxis || 'category') === 'value' || horizontal) ? zoomSliderPadding : 0);

  // An axis NAME centred BELOW the bottom axis needs its own room on top of the
  // legend so the two don't overlap — and so it isn't pushed off the canvas.
  // `containLabel` reserves room for the tick LABELS only; the centred name sits
  // nameGap px past the axis line, beyond those labels. Which axis is at the
  // bottom depends on orientation:
  //  - vertical   → the category axis (X) and its title,
  //  - horizontal → the value axis (X) and its title (e.g. "Revenue"). Without
  //    this band the horizontal value title was clipped below the tick labels
  //    (gridBottomBase 16 < nameGap 34) and simply never showed.
  const hasBottomAxisName = horizontal
    ? !valNameIsEnd && !!valAxisLabel
    : !!(catAxisLabel || capabilities.categoryLabel);

  // Reserve a small TOP band whenever an axis name floats at the top ('end'):
  //  - vertical  → the value-axis name (Y) sits top-left,
  //  - horizontal → the category-axis name (Y) sits top-left.
  // Keeps it from clipping and clear of the top-most gridline label.
  const hasTopValueName =
    (valNameIsEnd && !!(valAxisLabel || (dualAxis && valAxis2Label))) ||
    (catNameIsEndH && !!(catAxisLabel || capabilities.categoryLabel));
  const topValueNameBand = lx('valueNameTopBand', 18);
  // Horizontal + dual axis: the secondary value axis sits on TOP with its name
  // centred nameGap px above it — same clearance need as the bottom title.
  const hasTopSecondaryValueName = horizontal && dualAxis && !!valAxis2Label;

  return applyChartFont({
    // Top-level palette so ECharts cycles it (needed for `colorBy: 'data'` on a
    // single-measure bar, and a sensible default everywhere else).
    color: palette,
    animation: capabilities.animation !== false,
    tooltip: tooltip as unknown as EChartsOption['tooltip'],
    legend,
    ...(dataZoom ? { dataZoom: dataZoom as unknown as EChartsOption['dataZoom'] } : {}),
    grid: {
      left: (horizontal && hasHierarchy ? gridHierarchyLeft : 0) + gridLeft,
      right: (legendPos === 'right' && showLegend ? gridRightLegend : gridRightBase)
        + zoomRight
        // Room for horizontal bars' outside-end value labels (0 otherwise).
        + outsideLabelRightPad,
      top: (legendPos === 'top' && showLegend ? gridTopLegend : gridTopBase)
        + (hasTopValueName ? topValueNameBand : 0)
        + (hasTopSecondaryValueName ? gridBottomAxisName : 0),
      // Bottom room = base + legend band (if bottom) + axis-name band (if the
      // category axis is on the bottom and has a title) + zoom slider + hierarchy.
      bottom: gridBottomBase
        + (bottomLegend ? gridBottomLegend : 0)
        + (hasBottomAxisName ? gridBottomAxisName : 0)
        + zoomBottom
        + (!horizontal && hasHierarchy ? gridHierarchyBottom : 0),
      containLabel: true,
    },
    // Horizontal → value on X, category on Y. Vertical → category on X, value on Y.
    // The parent band axis (hierarchical) sits on the same physical side as the
    // child category axis.
    xAxis: horizontal
      ? (valAxes as unknown as EChartsOption['xAxis'])
      : ((parentBandAxis ? [catAxis, parentBandAxis] : catAxis) as unknown as EChartsOption['xAxis']),
    yAxis: horizontal
      ? ((parentBandAxis ? [catAxis, parentBandAxis] : catAxis) as unknown as EChartsOption['yAxis'])
      : (valAxes as unknown as EChartsOption['yAxis']),
    series: seriesList as unknown as EChartsOption['series'],
  }, capabilities.fontFamily, capabilities.fontSize, {
    // Per-axis build-form weights win for their own axis; the tile-wide Format
    // control fills whichever axes have no explicit choice. The secondary
    // (dual) value axis is index 1 on either side, so axis2 feeds x2 AND y2.
    x: capabilities.xAxisFontWeight || capabilities.axisFontWeight,
    y: capabilities.yAxisFontWeight || capabilities.axisFontWeight,
    x2: capabilities.axis2FontWeight || capabilities.axisFontWeight,
    y2: capabilities.axis2FontWeight || capabilities.axisFontWeight,
  });
}
